import { currencyGet, ApiShapeError } from '@/lib/task-api';
import { serverVolume, sumTotals } from '@/lib/report-volume';
import { endOfDayIso, startOfDayIso } from '@/lib/report-range';
import type { TotalAmount } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';

/**
 * ==================== BURO PANELI UCU (GET /v1/dashboards/exchange) =========
 *
 * CANLI SUNUCUYA KARSI DOGRULANMIS YANIT SEKLI:
 *
 *   { code: 'success', message: 'SUCCESSFULLY_DASHBOARD_DATA_GET',
 *     data: {
 *       accepted: { sum: 15960929, sums: { TRY: 15960929, USD: 287.5 },
 *                   currencies: { EUR: 5708286.5, USD: …, 'EUR/USD': 287.5 } },
 *       rejected: { … },
 *       warning:  { … } } }
 *
 * KOVALAR (2026-09-23, sunucu db.js readExchange):
 *   accepted = kazanan BENIM ve islem pending / payment_received / completed / resolved
 *   warning  = kazanan BENIM ve islem warning / refunded
 *   rejected = kazanan BASKA bir buro (durum ne olursa olsun)
 *
 * PARA BIRIMI (karar Q13): tutarlar KARSI para biriminde; EUR/USD tipinde USD, digerlerinde
 * TRY. Farkli birimler hicbir yerde tek sayida TOPLANMAZ — `sums` birim basina, `sum` yalniz
 * TRY (eski istemciler icin).
 *
 * Para birimi anahtarlari SERBEST METIN: canli veride 'EUR/USD' gibi capraz
 * pariteler de var, yani sabit bir birlesim tipine kapatilamaz.
 *
 * ------------------------------ KAPSAM -------------------------------------
 * SQL `task_offer.offer_user_id = <cagiran>` ile suzuyor: bu rakamlar
 * ORGANIZASYONUN degil, GIRIS YAPMIS KULLANICININ KENDI teklif cirosu.
 * (Organizasyon geneli icin /buro/raporlar ucları kullanilir.)
 *
 * -------------------------- HANGI PARAMETRELER -----------------------------
 * Sunucu `query`den YALNIZ `startDate`, `endDate`, `sendReport` okuyor;
 * kullanici kimligini `getCaller(headers)` ile TOKEN'dan cozuyor.
 *
 * `userId` ve `email` BILINCLI OLARAK GONDERILMIYOR (mobil istemci gonderiyor,
 * sunucu yok sayiyor). Ozellikle `email`: rapor ekinin gidecegi adres sunucuda
 * `db.dashboard.readUserEmail(callerUserId)` ile cagiranin KENDI kaydindan
 * cozuluyor. Istekten okunsaydi herkes bir baskasinin ciro raporunu istedigi
 * adrese yollatabilirdi — sunucudaki yorum bunu acikca anlatiyor. Gonderilmeyen
 * bir alan, ileride yanlislikla guvenilir hale getirilemez.
 *
 * `startDate`/`endDate` ZORUNLU: SQL `t.created_at >= $2 AND <= $3` yaziyor,
 * null gelirse karsilastirma NULL'a coker ve uc bos doner.
 * ========================================================================== */

export type DashboardStatusKey = 'accepted' | 'rejected' | 'warning';

/**
 * Durum sirasi SUNUCU ANAHTAR SIRASINA BIRAKILMAZ. `Object.keys` sirasi bir
 * sozlesme degil; ekranda kutularin yer degistirmesi kullanici icin veri
 * degismis gibi gorunur.
 */
export const DASHBOARD_STATUS_KEYS: readonly DashboardStatusKey[] = Object.freeze([
  'accepted',
  'rejected',
  'warning',
]);

/** Bir para biriminde toplam. Farkli para birimleri ASLA ayni sayida toplanmaz. */
export type CurrencyTotal = Readonly<{ currency: string; total: TotalAmount }>;

export type DashboardBucket = Readonly<{
  /**
   * KARSI PARA BIRIMI BASINA hacim (`sums`, 2026-09-23 karar Q13). Tutar `taskAmount *
   * offerPrice`, yani teklifin yazildigi birimde: EUR/USD'de USD, digerlerinde TRY.
   * Sira: TRY once, sonra alfabetik (bkz. byCurrencyOrder).
   */
  sums: readonly CurrencyTotal[];
  /**
   * ESKI alan — sunucu artik YALNIZ TRY toplamini yaziyor (magazadaki mobil surum onu "TRY"
   * etiketiyle gosteriyor). Web `sums` okur; bu alan geriye donuk dogrulama icin tutuluyor.
   */
  sum: TotalAmount;
  /**
   * Islem tipi -> hacim. Kod serbest metin ('USD', 'EUR/USD'…). Deger O TIPIN KARSI PARA
   * BIRIMINDE (`counterCurrency`): 'EUR/USD' satiri USD, digerleri TRY.
   */
  currencies: ReadonlyArray<Readonly<{ code: string; total: TotalAmount; counterCurrency: string }>>;
}>;

export type ExchangeDashboard = Readonly<{
  buckets: Readonly<Record<DashboardStatusKey, DashboardBucket>>;
  /**
   * Uc durumun toplami, PARA BIRIMI BASINA — ekrandaki "Toplam hacim". Eskiden tek bir
   * `grandTotal` vardi ve USD tutarlar TRY'ye eklenip "TRY" diye basiliyordu.
   */
  totals: readonly CurrencyTotal[];
}>;

type RawEnvelope = { code?: string; message?: string; data?: unknown };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** TRY once (panelin ana birimi), sonra alfabetik — sira sunucu anahtar sirasina BIRAKILMAZ. */
export const byCurrencyOrder = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a === 'TRY') return -1;
  if (b === 'TRY') return 1;
  return a.localeCompare(b);
};

/** Listede verilen para biriminin toplami; yoksa 0. */
export const totalIn = (list: readonly CurrencyTotal[], currency: string): TotalAmount =>
  list.find((item) => item.currency === currency)?.total ?? sumTotals([]);

/**
 * Para birimi bazli listeleri BIRIM BIRIM toplar. Ayni birimdeki degerler toplanir,
 * farkli birimler ayri satir kalir.
 */
export const mergeCurrencyTotals = (
  lists: ReadonlyArray<readonly CurrencyTotal[]>,
): CurrencyTotal[] => {
  const map = new Map<string, TotalAmount[]>();
  for (const list of lists) {
    for (const item of list) {
      map.set(item.currency, [...(map.get(item.currency) ?? []), item.total]);
    }
  }
  return [...map.entries()]
    .map(([currency, values]) => ({ currency, total: sumTotals(values) }))
    .sort((a, b) => byCurrencyOrder(a.currency, b.currency));
};

/**
 * BEKLENMEYEN SEKIL SESSIZCE SIFIRA DUSMEZ.
 *
 * `?? 0` yazmak cazip ama bir ciro panelinde tehlikeli: alan adi degisirse
 * (ornegin `sum` -> `totalSum`) ekran hatasiz calisir ve "0,00 TL" der.
 * Kullanici bunu "bu aralikta islem yok" diye okur. Sifir gosteren bir panel,
 * "yukleyemedim" diyen bir panelden DAHA KOTU. Bu yuzden eksik/yanlis her alan
 * ApiShapeError firlatir ve ekran hata durumunu cizer.
 */
const toBucket = (raw: unknown, key: DashboardStatusKey): DashboardBucket => {
  if (!isRecord(raw)) throw new ApiShapeError(`dashboards/exchange.data.${key} nesne degil`);

  const currenciesRaw = raw.currencies;
  if (!isRecord(currenciesRaw)) {
    throw new ApiShapeError(`dashboards/exchange.data.${key}.currencies nesne degil`);
  }

  const currencies = Object.keys(currenciesRaw).map((code) => ({
    code,
    total: serverVolume(currenciesRaw[code], `dashboards/exchange.data.${key}.currencies.${code}`),
    counterCurrency: pairOf(code).secondCurrency,
  }));

  const sum = serverVolume(raw.sum, `dashboards/exchange.data.${key}.sum`);

  // `sums` 2026-09-23'te geldi. HIC yoksa (eski sunucu) tek bilinen birim TRY toplami;
  // VAR ama nesne degilse sozlesme bozulmustur ve firlatilir.
  let sums: CurrencyTotal[];
  if (raw.sums === undefined) {
    sums = [{ currency: 'TRY', total: sum }];
  } else {
    const sumsRaw = raw.sums;
    if (!isRecord(sumsRaw)) {
      throw new ApiShapeError(`dashboards/exchange.data.${key}.sums nesne degil`);
    }
    sums = Object.keys(sumsRaw)
      .map((currency) => ({
        currency,
        total: serverVolume(sumsRaw[currency], `dashboards/exchange.data.${key}.sums.${currency}`),
      }))
      .sort((a, b) => byCurrencyOrder(a.currency, b.currency));
  }

  return {
    sums,
    sum,
    // Once karsi para birimi (TRY once), birim icinde buyukten kucuge: farkli birimleri
    // buyukluge gore karsilastirmak anlamsiz, ayni birimde "hangisi agir basiyor" okunur.
    currencies: currencies.sort(
      (a, b) => byCurrencyOrder(a.counterCurrency, b.counterCurrency) || b.total - a.total,
    ),
  };
};

const toDashboard = (payload: RawEnvelope): ExchangeDashboard => {
  const { data } = payload;
  if (!isRecord(data)) throw new ApiShapeError('dashboards/exchange.data nesne degil');

  const buckets = {} as Record<DashboardStatusKey, DashboardBucket>;
  for (const key of DASHBOARD_STATUS_KEYS) buckets[key] = toBucket(data[key], key);

  return {
    buckets,
    totals: mergeCurrencyTotals(DASHBOARD_STATUS_KEYS.map((key) => buckets[key].sums)),
  };
};

export type DashboardRangeInput = Readonly<{ start: Date; end: Date }>;

/** Panel verisi. Rapor e-postasi GONDERMEZ — o ayri bir eylem (asagida). */
export const fetchExchangeDashboard = async (
  range: DashboardRangeInput,
): Promise<ExchangeDashboard> => {
  const { data } = await currencyGet<RawEnvelope>('/v1/dashboards/exchange', {
    startDate: startOfDayIso(range.start),
    endDate: endOfDayIso(range.end),
  });
  return toDashboard(data);
};

/**
 * XLSX RAPORUNU E-POSTALA.
 *
 * AYRI BIR CAGRI ve bilincli olarak `useQuery` DEGIL `useMutation` ile
 * kullanilir: e-posta gondermek bir YAN ETKI. Ayni ucu `sendReport` ile
 * sorgudan cagirmak, React Query'nin her yeniden denemesinde/odak
 * tazelemesinde kullaniciya bir e-posta daha yollardi.
 *
 * ALICI ADRESI GONDERILMEZ — sunucu cagiranin kendi kaydindan cozer.
 */
export const sendDashboardReport = async (range: DashboardRangeInput): Promise<void> => {
  await currencyGet<RawEnvelope>('/v1/dashboards/exchange', {
    startDate: startOfDayIso(range.start),
    endDate: endOfDayIso(range.end),
    sendReport: 'true',
  });
};

/** Testler ve teshis icin: ham zarftan gorunum modeline donusum. */
export const parseExchangeDashboard = toDashboard;
